import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ChevronDown, FlaskConical } from 'lucide-react';
import {
  type AccountTransaction,
  transactionChildWithContext,
} from '@mls-wallet/shared-types';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/status-badge';
import { OriginDisplay, DestinationDisplay } from './transaction-displays';
import { TransactionDetailTypeDisplay } from './transaction-detail-type-display';
import { ContractInteractionDetails } from './contract-interaction-details';
import {
  canSimulateStoredTransaction,
  formatTransactionDate,
  formatTransactionExpirationTimestamp,
} from './utils';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { TransactionSimulationSheet } from '@/components/transaction-builder/transaction-simulation/TransactionSimulationSheet';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { useQueuedTransactions } from '@/hooks/transaction/use-queued-transactions';
import { useExecuteAccountTransaction } from '@/hooks/transaction/use-execute-account-transaction';
import { useExecution } from '@/components/execution/execution-context';
import { useAuthClient } from '@/lib/authClient';
import type { TransactionSignatureIntent } from '@/hooks/transaction/use-create-transaction-signature';
import { toPendingTransactionSignables } from './batch-transaction-utils';

interface Props {
  transaction: AccountTransaction | null;
  transactions: AccountTransaction[];
  refreshQueuedTransactions: () => Promise<void>;
  refreshAllTransactions: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (
    transaction: AccountTransaction
  ) => Promise<TransactionSignatureIntent[]>;
  onReject: (
    transaction: AccountTransaction,
    reason: string
  ) => Promise<TransactionSignatureIntent[]>;
}

/** Parent actions require every child's quorum for the requested decision. */
export function BatchTransactionDetailSheet({
  transaction,
  transactions,
  refreshQueuedTransactions,
  refreshAllTransactions,
  isOpen,
  onOpenChange,
  onApprove,
  onReject,
}: Props) {
  const [simulationId, setSimulationId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const simulationButton = useRef<HTMLButtonElement>(null);
  const pending = usePendingSignaturesState();
  const { user } = useAuthClient();
  const { executeTransaction, isLoading: executing } =
    useExecuteAccountTransaction();
  const { startExecution } = useExecution();
  const id = transaction?.id;
  const canSimulate = transaction
    ? canSimulateStoredTransaction(transaction)
    : false;
  useEffect(() => {
    // A nested simulation belongs to one open parent, never to a later selection.
    if (!isOpen || !canSimulate || simulationId !== id) setSimulationId(null);
    setActionError(null);
  }, [isOpen, canSimulate, id, simulationId]);

  const requestSignatures = async (decision: 'APPROVE' | 'REJECT') => {
    if (!transaction) return;
    setRequesting(true);
    setActionError(null);
    try {
      const signatures =
        decision === 'APPROVE'
          ? await onApprove(transaction)
          : await onReject(transaction, '');
      await refreshQueuedTransactions();
      // Only poll the child IDs the server actually authorized for this member.
      pending.setPendingSignableIds(
        signatures.map((signature) => signature.proposalId)
      );
      pending.setPendingDecision(decision);
      pending.setIsPendingModalOpen(true);
      onOpenChange(false);
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'Unable to request signatures. Please try again.'
      );
    } finally {
      setRequesting(false);
    }
  };
  const execute = (action: 'APPROVE' | 'REJECT') => {
    if (!transaction) return;
    const executionOptions = {
      executionTarget: 'transaction' as const,
      // The execution tray tracks the parent once for all of its children.
      executionTargetId: transaction.id,
      request: () =>
        executeTransaction({ transactionId: transaction.id, action }),
      onSuccess: refreshAllTransactions,
    };
    startExecution(executionOptions);
    onOpenChange(false);
  };
  const approvalReady =
    transaction?.signatureStatus === 'approvalReady' ||
    transaction?.signatureStatus === 'approvalAndRejectionReady';
  const rejectionReady =
    transaction?.signatureStatus === 'rejectionReady' ||
    transaction?.signatureStatus === 'approvalAndRejectionReady';
  const actionable =
    transaction &&
    !transaction.invalidated &&
    transaction.signatureStatus !== 'expired' &&
    (transaction.executionStatus === null ||
      transaction.executionStatus === 'blocked');
  const canApprove = transaction?.children.some(
    (child) =>
      child.policy.requiredApprovals > 0 &&
      child.pendingApprovers.some((member) => member.id === user?.id) &&
      !child.approvedMembers.some((member) => member.id === user?.id)
  );
  const canReject = transaction?.children.some(
    (child) =>
      // The backend supplies rejection eligibility for each child's policy.
      child.pendingRejectors.some((member) => member.id === user?.id) &&
      !child.rejectedMembers.some((member) => member.id === user?.id)
  );
  const overLimit = transaction?.children.some(
    (child) => child.overPolicyLimit
  );

  return (
    <>
      <PendingSignaturesModal
        open={pending.isPendingModalOpen}
        resetPendingState={pending.resetPendingState}
        refreshSignables={refreshQueuedTransactions}
        pendingSignableIds={pending.pendingSignableIds}
        polledSignables={toPendingTransactionSignables(transactions)}
        pendingDecision={pending.pendingDecision}
        hookFunctionToPoll={useQueuedTransactions}
        isTransaction
      />
      <Sheet open={isOpen && Boolean(transaction)} onOpenChange={onOpenChange}>
        {transaction && (
          <SheetContent
            className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl"
            aria-describedby="batch-transaction-description"
          >
            <SheetHeader className="space-y-4 border-b p-[30px]">
              <p className="pr-8 text-xs text-muted-foreground break-all">
                TRANSACTION #{transaction.id}
              </p>
              <SheetTitle className="flex items-center gap-2 text-base">
                <ArrowUpRight className="size-4 text-rose-400" />
                {transaction.children.length > 1
                  ? `Bulk transaction (${transaction.children.length} transactions)`
                  : 'Transaction'}
              </SheetTitle>
              <SheetDescription
                id="batch-transaction-description"
                className="truncate"
              >
                {transaction.description}
              </SheetDescription>
              <div className="flex flex-wrap items-stretch overflow-hidden rounded-xl border text-xs">
                <StatusBadge
                  status={
                    transaction.executionStatus ?? transaction.signatureStatus
                  }
                  invalidated={transaction.invalidated}
                  className="rounded-none px-5"
                />
                <div className="space-y-1 border-l px-4 py-3">
                  <p className="text-muted-foreground">Initiated by</p>
                  <p>{transaction.initiator.name}</p>
                </div>
                <div className="space-y-1 border-l px-4 py-3">
                  <p className="text-muted-foreground">Initiated on</p>
                  <p>{formatTransactionDate(transaction.dateProposed)}</p>
                </div>
                <div className="space-y-1 border-l px-4 py-3">
                  <p className="text-muted-foreground">Expires on</p>
                  <p>
                    {formatTransactionExpirationTimestamp(
                      transaction.expirationTimestamp
                    )}
                  </p>
                </div>
              </div>
              <OriginDisplay account={transaction.account} variant="detail" />
            </SheetHeader>
            <div className="flex-1 space-y-[30px] overflow-y-auto bg-muted p-[30px]">
              <section>
                <h2 className="mb-2 px-5 text-xs text-muted-foreground">
                  Description
                </h2>
                <p className="whitespace-pre-wrap break-words rounded-xl border bg-background px-6 py-5 text-sm">
                  {transaction.description}
                </p>
              </section>
              <section aria-label="Batch transactions">
                <h2 className="mb-2 px-5 text-xs text-muted-foreground">
                  Transactions ({transaction.children.length})
                </h2>
                <div className="divide-y overflow-hidden rounded-xl border bg-background">
                  {transaction.children.map((child, index) => {
                    const details = transactionChildWithContext(
                      transaction,
                      child
                    );
                    return (
                      <details key={child.id} className="group">
                        <summary
                          aria-label={`Transaction ${index + 1}: ${child.type === 'TOKEN_TRANSFER' ? 'Token Transfer' : 'Contract Interaction'}`}
                          className="flex cursor-pointer list-none items-center gap-5 px-[25px] py-6 [&::-webkit-details-marker]:hidden"
                        >
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
                              <span>
                                {child.type === 'TOKEN_TRANSFER'
                                  ? 'Token Transfer'
                                  : 'Contract Interaction'}
                              </span>
                              <span>
                                {child.policy.name} ·{' '}
                                {child.policy.requiredApprovals === 0
                                  ? 'Automatic approval'
                                  : `${child.approvedMembers.length}/${child.policy.requiredApprovals} approvals`}
                                {child.rejectedMembers.length > 0 &&
                                  ` · ${child.rejectedMembers.length}/${Math.max(1, child.policy.requiredApprovals)} rejections`}
                              </span>
                            </div>
                            <TransactionDetailTypeDisplay
                              transaction={details}
                              variant="detail"
                            />
                          </div>
                          <ChevronDown
                            aria-hidden
                            className="size-6 shrink-0 transition-transform group-open:rotate-180"
                          />
                        </summary>
                        <div className="space-y-4 border-t bg-muted/40 px-[25px] py-5">
                          <div className="rounded-lg border bg-muted px-5 py-4 text-sm">
                            <div className="flex flex-wrap justify-between gap-2">
                              <span>Policy: {child.policy.name}</span>
                              <span>
                                {child.policy.requiredApprovals === 0
                                  ? 'Only initiator'
                                  : `${child.policy.requiredApprovals} of ${child.policy.totalApprovers} required`}
                              </span>
                            </div>
                            {child.policy.description && (
                              <p className="mt-2 whitespace-pre-wrap text-muted-foreground">
                                {child.policy.description}
                              </p>
                            )}
                          </div>
                          {details.type === 'TOKEN_TRANSFER' ? (
                            <DestinationDisplay
                              type={details.type}
                              destination={details.destination}
                              destinationName={details.destinationName}
                              networkId={transaction.networkId}
                              variant="detail"
                              showCopy
                            />
                          ) : (
                            <ContractInteractionDetails transaction={details} />
                          )}
                          {child.policy.requiredApprovals > 0 && (
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <h3 className="mb-2 text-muted-foreground">
                                  Approved
                                </h3>
                                {child.approvedMembers.map((member) => (
                                  <p key={member.id}>{member.name}</p>
                                ))}
                                {child.approvedMembers.length === 0 && (
                                  <p>No approvals</p>
                                )}
                              </div>
                              <div>
                                <h3 className="mb-2 text-muted-foreground">
                                  Pending
                                </h3>
                                {child.pendingApprovers.map((member) => (
                                  <p key={member.id}>{member.name}</p>
                                ))}
                                {child.pendingApprovers.length === 0 && (
                                  <p>No pending members</p>
                                )}
                              </div>
                            </div>
                          )}
                          {child.rejectedMembers.length > 0 && (
                            <div className="text-sm">
                              <h3 className="mb-2 text-muted-foreground">
                                Rejected
                              </h3>
                              {child.rejectedMembers.map((member) => (
                                <p key={member.id}>{member.name}</p>
                              ))}
                            </div>
                          )}
                          {child.overPolicyLimit && (
                            <p
                              role="status"
                              className="text-sm text-destructive"
                            >
                              This transaction exceeds its policy’s current
                              limit.
                            </p>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </section>
            </div>
            <SheetFooter className="flex-row flex-wrap items-center justify-end gap-3 border-t p-[30px]">
              {actionError && (
                <p role="alert" className="w-full text-sm text-destructive">
                  {actionError}
                </p>
              )}
              {canSimulate && (
                <Button
                  ref={simulationButton}
                  variant="outline"
                  className="mr-auto"
                  onClick={() => setSimulationId(transaction.id)}
                >
                  <FlaskConical className="size-4" />
                  Simulate
                </Button>
              )}
              {actionable && (
                <Button
                  variant="outline"
                  disabled={
                    requesting || executing || (!rejectionReady && !canReject)
                  }
                  onClick={() =>
                    rejectionReady
                      ? execute('REJECT')
                      : void requestSignatures('REJECT')
                  }
                >
                  {rejectionReady ? 'Execute rejection' : 'Reject'}
                </Button>
              )}
              {actionable &&
                (approvalReady ? (
                  <Button
                    disabled={executing || requesting || overLimit}
                    onClick={() => execute('APPROVE')}
                  >
                    Execute
                  </Button>
                ) : (
                  <Button
                    disabled={
                      !canApprove ||
                      requesting ||
                      transaction.signatureStatus ===
                        'pendingInitiatorSignature'
                    }
                    onClick={() => void requestSignatures('APPROVE')}
                  >
                    {canApprove ? 'Approve' : 'Waiting for approvals'}
                  </Button>
                ))}
            </SheetFooter>
          </SheetContent>
        )}
        {/* Nest the simulation under this dialog root so Radix preserves the parent focus scope. */}
        {transaction && canSimulate && (
          <TransactionSimulationSheet
            source="stored"
            transactionId={transaction.id}
            networkId={transaction.networkId}
            open={isOpen && simulationId === transaction.id}
            onOpenChange={(open) =>
              setSimulationId(open ? transaction.id : null)
            }
            returnFocusRef={simulationButton}
          />
        )}
      </Sheet>
    </>
  );
}
